/**
 * Integrations Settings Page - Chorus Pro + Intelligence Artificielle
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Network, Bot, Shield } from 'lucide-react';
import { Spinner } from '@/components/ui-v2';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { ChorusProSettings } from '@/features/compliance/components/ChorusProSettings';
import { useTreasuryConfig, useUpdateTreasuryConfig } from '@/features/treasury/hooks/useTreasury';
import { notifications } from '@/lib/notifications';

const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
  border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
  transition-all duration-300`;

export const IntegrationsSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { company, loading, error } = useCompanySettings();

  // AI Consent Configuration
  const {
    data: treasuryConfig,
    isLoading: configLoading,
  } = useTreasuryConfig(company?.id || '');
  const updateConfigMutation = useUpdateTreasuryConfig();
  const [aiConsentEnabled, setAiConsentEnabled] = useState(false);

  // Sync AI consent state with config
  useEffect(() => {
    if (treasuryConfig) {
      setAiConsentEnabled(treasuryConfig.aiRecommendationsEnabled ?? false);
    }
  }, [treasuryConfig]);

  const handleToggleAiConsent = async (enabled: boolean) => {
    if (!company?.id) return;
    setAiConsentEnabled(enabled);
    try {
      await updateConfigMutation.mutateAsync({
        companyId: company.id,
        config: { aiRecommendationsEnabled: enabled },
      });
      notifications.saved(t('settings.integrations.aiConsent'));
    } catch (err: any) {
      setAiConsentEnabled(!enabled); // Revert on error
      notifications.saveError(t('settings.integrations.aiConsent'), err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className={cardClasses}>
        <div className="text-center py-8">
          <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
            <Network className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {error || t('settings.company.noCompany')}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-100 rounded-xl">
          <Network className="w-5 h-5 text-indigo-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.integrations.title')}</h2>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Chorus Pro */}
        <ChorusProSettings />

        {/* Intelligence Artificielle - RGPD Consent */}
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <div className="p-2 bg-violet-100 rounded-xl">
              <Bot className="w-5 h-5 text-violet-600" />
            </div>
            {t('settings.integrations.ai')}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('settings.integrations.aiDesc')}
          </p>

          {configLoading ? (
            <div className="flex justify-center py-4">
              <Spinner size="sm" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-violet-50/50 border border-violet-200/50 rounded-2xl p-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiConsentEnabled}
                    onChange={(e) => handleToggleAiConsent(e.target.checked)}
                    disabled={updateConfigMutation.isPending}
                    className="w-5 h-5 text-violet-600 rounded focus:ring-2 focus:ring-violet-500"
                  />
                  <div className="ml-3 flex-1">
                    <span className="block text-sm font-medium text-gray-900">
                      {t('settings.integrations.enableAiRecommendations')}
                    </span>
                    <span className="block text-xs text-gray-600 mt-1">
                      {t('settings.integrations.enableAiRecommendationsDesc')}
                    </span>
                  </div>
                </label>
              </div>

              <div className="bg-amber-50/50 border border-amber-200/50 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-amber-100 rounded-lg">
                    <Shield className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">{t('settings.integrations.gdprCompliance')}</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>{t('settings.integrations.gdpr1')}</li>
                      <li>{t('settings.integrations.gdpr2')}</li>
                      <li>{t('settings.integrations.gdpr3')}</li>
                      <li>{t('settings.integrations.gdpr4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsSettingsPage;
